import React, { useState, useEffect } from 'react';
import { Layout } from "./components/dashboard/layout";

const StaffAttendanceDashboard = () => {
  const [staff, setStaff] = useState([]);
  const [departmentSummary, setDepartmentSummary] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStaffData = async () => {
    setIsLoading(true);
    try {
      // Replace with your actual API endpoints
      const staffResponse = await fetch('https://raptornet.aischennai.org/screening-manager/gate-api/getStaffAttendance.php');
      const summaryResponse = await fetch('https://raptornet.aischennai.org/screening-manager/gate-api/getDepartmentSummary.php');
      
      const staffData = await staffResponse.json();
      const summaryData = await summaryResponse.json();

      if (staffData.success && Array.isArray(staffData.data)) {
        setStaff(staffData.data);
      } else {
        setStaff([]);
      }

      if (summaryData.success && Array.isArray(summaryData.data)) {
        setDepartmentSummary(summaryData.data);
      } else {
        setDepartmentSummary([]);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching staff attendance data:", error);
      setStaff([]);
      setDepartmentSummary([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getAttendanceStatus = (checkinTime, checkoutTime) => {
    if (checkinTime && checkoutTime) return 'Checked Out';
    if (checkinTime) return 'On Campus';
    return 'Not Checked In';
  };

  const getStatusColor = (checkinTime, checkoutTime) => {
    if (checkinTime && checkoutTime) return '#f44336'; // Red - checked out
    if (checkinTime) return '#4caf50'; // Green - on campus
    return '#ff9800'; // Orange - not checked in
  };

  const styles = {
    container: {
      padding: '2rem',
      maxWidth: '100%'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '2rem',
      fontWeight: 'bold',
      margin: 0,
      color: '#333'
    },
    refreshButton: {
      padding: '10px 20px',
      backgroundColor: '#1976d2',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      minWidth: '120px'
    },
    refreshButtonDisabled: {
      backgroundColor: '#ccc',
      cursor: 'not-allowed'
    },
    lastUpdated: {
      fontSize: '0.875rem',
      color: '#666',
      marginBottom: '1rem'
    },
    tabs: {
      display: 'flex',
      marginBottom: '2rem',
      borderBottom: '2px solid #e0e0e0'
    },
    tab: {
      padding: '12px 24px',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
      borderBottom: '2px solid transparent',
      marginRight: '8px'
    },
    activeTab: {
      borderBottom: '2px solid #1976d2',
      color: '#1976d2',
      fontWeight: '600'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem'
    },
    summaryCard: {
      backgroundColor: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '1.5rem',
      textAlign: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      minHeight: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    departmentTitle: {
      fontSize: '1.1rem',
      color: '#1976d2',
      marginBottom: '8px',
      fontWeight: '600'
    },
    attendanceNumber: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#4caf50',
      marginBottom: '4px'
    },
    attendanceText: {
      fontSize: '0.875rem',
      color: '#666',
      marginBottom: '4px'
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#333'
    },
    tableContainer: {
      overflowX: 'auto',
      marginTop: '1rem'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '14px',
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    headerCell: {
      padding: '12px 16px',
      borderBottom: '2px solid #ddd',
      fontWeight: '600',
      color: '#333',
      backgroundColor: '#f8f9fa',
      whiteSpace: 'nowrap'
    },
    cell: {
      padding: '10px 16px',
      borderBottom: '1px solid #eee',
      color: '#444',
      whiteSpace: 'nowrap'
    },
    evenRow: {
      backgroundColor: '#fafafa'
    },
    oddRow: {
      backgroundColor: '#ffffff'
    },
    noData: {
      textAlign: 'center',
      padding: '20px',
      color: '#666'
    }
  };

  useEffect(() => {
    fetchStaffData();
  }, []);

  return (
    <Layout>
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Staff Attendance Dashboard</h1>
        <button 
          style={{
            ...styles.refreshButton,
            ...(isLoading ? styles.refreshButtonDisabled : {})
          }}
          onClick={fetchStaffData} 
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {lastUpdated && (
        <div style={styles.lastUpdated}>
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      )}

      <div style={styles.tabs}>
        <button 
          style={{
            ...styles.tab,
            ...(activeTab === 0 ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab(0)}
        >
          Department Summary
        </button>
        <button 
          style={{
            ...styles.tab,
            ...(activeTab === 1 ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab(1)}
        >
          Staff Details
        </button>
      </div>

      {activeTab === 0 && (
        <div>
          <h2 style={styles.sectionTitle}>Department-wise Attendance Summary</h2>
          
          {departmentSummary.length === 0 ? (
            <div style={styles.noData}>No summary data available.</div>
          ) : (
            <>
              <div style={styles.summaryGrid}>
                {departmentSummary.map((dept, idx) => (
                  <div key={idx} style={styles.summaryCard}>
                    <div style={styles.departmentTitle}>
                      {dept.department || 'Unknown Department'}
                    </div>
                    <div style={styles.attendanceNumber}>
                      {dept.checked_in_staff || 0}
                    </div>
                    <div style={styles.attendanceText}>
                      of {dept.total_staff || 0} staff present
                    </div>
                    <div style={{...styles.attendanceText, fontSize: '0.75rem'}}>
                      ({dept.checkin_percentage || 0}% attendance)
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.headerCell}>Department</th>
                      <th style={styles.headerCell}>Total Staff</th>
                      <th style={styles.headerCell}>Checked In</th>
                      <th style={styles.headerCell}>Checked Out</th>
                      <th style={styles.headerCell}>Not Checked In</th>
                      <th style={styles.headerCell}>Still on Campus</th>
                      <th style={styles.headerCell}>Check-in %</th>
                      <th style={styles.headerCell}>Check-out %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departmentSummary.map((dept, idx) => (
                      <tr key={idx} style={idx % 2 === 0 ? styles.evenRow : styles.oddRow}>
                        <td style={{...styles.cell, fontWeight: '600'}}>{dept.department || 'Unknown'}</td>
                        <td style={styles.cell}>{dept.total_staff || 0}</td>
                        <td style={{...styles.cell, color: '#4caf50', fontWeight: '600'}}>{dept.checked_in_staff || 0}</td>
                        <td style={{...styles.cell, color: '#f44336', fontWeight: '600'}}>{dept.checked_out_staff || 0}</td>
                        <td style={{...styles.cell, color: '#ff9800', fontWeight: '600'}}>{dept.not_checked_in || 0}</td>
                        <td style={{...styles.cell, color: '#2196f3', fontWeight: '600'}}>{dept.still_on_campus || 0}</td>
                        <td style={styles.cell}>{dept.checkin_percentage || 0}%</td>
                        <td style={styles.cell}>{dept.checkout_percentage || 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 1 && (
        <div>
          <h2 style={styles.sectionTitle}>Staff Attendance Details</h2>
          
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.headerCell}>PS Code</th>
                  <th style={styles.headerCell}>Department</th>
                  <th style={styles.headerCell}>First Name</th>
                  <th style={styles.headerCell}>Last Name</th>
                  <th style={styles.headerCell}>Job Title</th>
                  <th style={styles.headerCell}>Status</th>
                  <th style={styles.headerCell}>Check-In Time</th>
                  <th style={styles.headerCell}>Check-In Device</th>
                  <th style={styles.headerCell}>Check-Out Time</th>
                  <th style={styles.headerCell}>Check-Out Device</th>
                </tr>
              </thead>
              <tbody>
                {staff.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={styles.noData}>
                      {isLoading ? 'Loading staff data...' : 'No staff data available.'}
                    </td>
                  </tr>
                ) : (
                  staff.map((staffMember, idx) => (
                    <tr key={idx} style={idx % 2 === 0 ? styles.evenRow : styles.oddRow}>
                      <td style={styles.cell}>{staffMember.ps_code || '-'}</td>
                      <td style={styles.cell}>{staffMember.department || '-'}</td>
                      <td style={styles.cell}>{staffMember.first_name || '-'}</td>
                      <td style={styles.cell}>{staffMember.last_name || '-'}</td>
                      <td style={styles.cell}>{staffMember.job_title || '-'}</td>
                      <td style={{
                        ...styles.cell,
                        color: getStatusColor(staffMember.checkin_time, staffMember.checkout_time),
                        fontWeight: '600'
                      }}>
                        {getAttendanceStatus(staffMember.checkin_time, staffMember.checkout_time)}
                      </td>
                      <td style={styles.cell}>
                        {staffMember.checkin_time ? new Date(staffMember.checkin_time).toLocaleTimeString() : '-'}
                      </td>
                      <td style={styles.cell}>{staffMember.checkin_device_name || '-'}</td>
                      <td style={styles.cell}>
                        {staffMember.checkout_time ? new Date(staffMember.checkout_time).toLocaleTimeString() : '-'}
                      </td>
                      <td style={styles.cell}>{staffMember.checkout_device_name || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default StaffAttendanceDashboard;